import { AvailabilitiesResponse } from '../../types'

/**
 * Merges Asego times into existing availability data
 *
 * This function is specifically designed for merging Asego times (fetched when user clicks on a day)
 * into the existing merged availability data. It keeps existing time slots and adds Asego slots
 * that don't already exist (deduplicated by date_start and date_end).
 *
 * The merge strategy:
 * - Keeps existing time blocks for resources/days
 * - Adds Asego time blocks that don't already exist (deduplicated by date_start/date_end)
 * - Adds new resources/days from Asego times if they don't exist
 * - Preserves all other existing data
 * - Merges resourceHasCalendarIntegration flags
 */
const mergeAsegoTimes = (
  existingData: AvailabilitiesResponse | undefined,
  asegoTimes: AvailabilitiesResponse | undefined
): AvailabilitiesResponse | undefined => {
  if (!existingData) {
    return asegoTimes
  }

  if (!asegoTimes) {
    return existingData
  }

  const merged: AvailabilitiesResponse = {
    dayAvailability: { ...existingData.dayAvailability },
    responseStatus: existingData.responseStatus,
    resourceHasCalendarIntegration: {
      ...existingData.resourceHasCalendarIntegration,
      ...asegoTimes.resourceHasCalendarIntegration,
    },
    hasAppointmentTypeMappingError: existingData.hasAppointmentTypeMappingError || asegoTimes.hasAppointmentTypeMappingError,
    firstAvailability: existingData.firstAvailability || asegoTimes.firstAvailability,
  }

  // Merge Asego times - keep existing slots and add new ones from Asego
  Object.keys(asegoTimes.dayAvailability).forEach((dayKey) => {
    if (!merged.dayAvailability[dayKey]) {
      merged.dayAvailability[dayKey] = {}
    }

    Object.keys(asegoTimes.dayAvailability[dayKey]).forEach((resourceId) => {
      const existingSlots = merged.dayAvailability[dayKey][resourceId] || []
      const asegoSlots = asegoTimes.dayAvailability[dayKey][resourceId] || []

      // Create a Set of existing slot keys for fast lookup
      const existingSlotKeys = new Set(existingSlots.map((slot) => `${slot.date_start.toISOString()}-${slot.date_end.toISOString()}`))

      // Add Asego slots that don't already exist
      const newAsegoSlots = asegoSlots.filter(
        (slot) => !existingSlotKeys.has(`${slot.date_start.toISOString()}-${slot.date_end.toISOString()}`)
      )

      // Combine existing slots with new Asego slots
      merged.dayAvailability[dayKey][resourceId] = [...existingSlots, ...newAsegoSlots]
    })
  })

  return merged
}

export { mergeAsegoTimes }
